import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Spinner } from "@/shared/components/ui/spinner";
import { formatDateTime } from "@/shared/lib/formatters";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { stockService } from "@/features/stocks/services/stock.service";
import { stockAdminService } from "@/features/stocks/services/stock-admin.service";
import type { StockResponseDTO } from "@/features/stocks/types/stock.types";
import { getStockConditionLabel } from "@/features/stocks/utils/stock.utils";
import { getApiErrorMessage } from "@/shared/lib/api-error";

export function EstoqueDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const [stock, setStock] = useState<StockResponseDTO | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const stockId = id ? Number(id) : NaN;
  const isValidId = Number.isInteger(stockId) && stockId > 0;

  const load = useCallback(async () => {
    if (!isValidId) return;
    setLoading(true);
    setError(null);
    try {
      const service = isAdmin ? stockAdminService : stockService;
      const data = await service.getById(stockId);
      setStock(data);
    } catch (error: unknown) {
      const message = getApiErrorMessage(error, "Erro ao carregar peça");
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [stockId, isValidId, isAdmin]);

  useEffect(() => {
    // load() dispara setLoading(true) síncrono para spinner imediato;
    // não causa cascading render porque loading não está nas deps.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    load();
  }, [load]);

  return (
    <div className="space-y-6">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate(-1)}
        className="gap-2"
      >
        <ArrowLeft className="w-4 h-4" />
        Voltar
      </Button>

      <Card>
        <CardHeader>
          <CardTitle>
            {stock ? `Peça #${stock.id}` : "Detalhes da peça"}
          </CardTitle>
          <CardDescription>
            {stock
              ? `${stock.barcode} · ${stock.productName}`
              : "Carregando informações da peça."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!isValidId ? (
            <div className="py-16 text-center text-muted-foreground">
              ID inválido.
            </div>
          ) : loading && !stock ? (
            <div className="flex justify-center items-center py-16">
              <Spinner className="w-6 h-6" />
            </div>
          ) : error && !stock ? (
            <div className="flex flex-col items-center gap-3 py-16 text-center">
              <p className="text-destructive">{error}</p>
              <Button variant="outline" size="sm" onClick={load}>
                Tentar novamente
              </Button>
            </div>
          ) : !stock ? (
            <div className="py-16 text-center text-muted-foreground">
              Peça não encontrada.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-[auto,1fr] gap-6">
              {stock.photoUrl ? (
                <img
                  src={stock.photoUrl}
                  alt={stock.productName}
                  className="w-full md:w-64 aspect-square rounded-md object-cover border border-border"
                />
              ) : (
                <div className="w-full md:w-64 aspect-square rounded-md bg-muted flex items-center justify-center text-sm text-muted-foreground border border-border">
                  Sem foto
                </div>
              )}

              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 content-start">
                <div className="space-y-0.5">
                  <dt className="text-xs text-muted-foreground">
                    Código de barras
                  </dt>
                  <dd className="text-sm font-medium">{stock.barcode}</dd>
                </div>
                <div className="space-y-0.5">
                  <dt className="text-xs text-muted-foreground">Produto</dt>
                  <dd className="text-sm font-medium">{stock.productName}</dd>
                </div>
                <div className="space-y-0.5">
                  <dt className="text-xs text-muted-foreground">Tamanho</dt>
                  <dd className="text-sm font-medium">{stock.size}</dd>
                </div>
                <div className="space-y-0.5">
                  <dt className="text-xs text-muted-foreground">Condição</dt>
                  <dd className="text-sm font-medium">
                    {getStockConditionLabel(stock.condition)}
                  </dd>
                </div>
                <div className="space-y-0.5">
                  <dt className="text-xs text-muted-foreground">
                    Cadastrado em
                  </dt>
                  <dd className="text-sm font-medium">
                    {formatDateTime(stock.createdAt)}
                  </dd>
                </div>
                <div className="space-y-0.5">
                  <dt className="text-xs text-muted-foreground">
                    Atualizado em
                  </dt>
                  <dd className="text-sm font-medium">
                    {formatDateTime(stock.updatedAt)}
                  </dd>
                </div>
              </dl>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
